import type { ReactNode } from "react";
import { Flex, Stack } from "@/components/layout";
import { Alert, Button, LoadingState, Select } from "@/components/ui";
import { INSIGHT_RANGES, type InsightRange } from "./api";
import type { InsightState } from "./useInsight";

export function RangeSelect({
  value,
  onChange,
  id = "insight-range",
}: {
  value: InsightRange;
  onChange: (next: InsightRange) => void;
  id?: string;
}) {
  return (
    <Select
      id={id}
      label="Time range"
      value={String(value)}
      onChange={(e) => onChange(Number(e.target.value) as InsightRange)}
      options={INSIGHT_RANGES.map((r) => ({ value: String(r.value), label: r.label }))}
    />
  );
}

/**
 * The states every insight screen shares: first load, failure with retry, and
 * the data itself. While a range change is reloading, the previous data stays
 * on screen under a small "Refreshing" note rather than blanking.
 */
export function InsightBoundary<T>({
  state,
  what,
  children,
}: {
  state: InsightState<T>;
  what: string;
  children: (data: T) => ReactNode;
}) {
  const { data, error, loading, reload } = state;

  if (!data && loading) return <LoadingState message={`Loading ${what}…`} />;

  if (!data && error) {
    return (
      <Stack gap={12}>
        <Alert tone="danger" title={`Could not load ${what}`}>
          {error}
        </Alert>
        <Flex gap={8}>
          <Button variant="secondary" onClick={reload}>
            Retry
          </Button>
        </Flex>
      </Stack>
    );
  }

  if (!data) return null;

  return (
    <Stack gap={24}>
      {error ? (
        <Alert tone="warning" title="Showing the last data that loaded">
          The refresh failed: {error}{" "}
          <Button variant="ghost" size="sm" onClick={reload}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {loading ? <LoadingState message="Refreshing…" /> : null}
      {children(data)}
    </Stack>
  );
}
